import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { hoyChicago } from "@/lib/format";
import { TIPO_DOC_OFICINA } from "@/lib/constants";
import { etapasNuevas, cambiarFlujo, armarPayload, validarCarga } from "@/lib/etapas";
import { Button } from "@/components/ui/button";
import { PageHeader, SectionCard } from "@/components/marca/Layout";
import { DatosCarga } from "@/components/panel/DatosCarga";
import { EtapasEditor } from "@/components/panel/EtapasEditor";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Loader2, Upload, FileText, X, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { t } from "@/lib/i18n";

// Fix P (2026-10-13): la edición de una carga ahora se hace en el expediente (/panel/cargas/:id). La ruta vieja redirige.
export default function CargaForm() {
  const { id } = useParams();
  if (id) return <Navigate to={`/panel/cargas/${id}`} replace />;
  return <NuevaCarga />;
}

function NuevaCarga() {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [choferes, setChoferes] = useState([]);
  const [ubicaciones, setUbicaciones] = useState([]);
  const [datos, setDatos] = useState({ load_id: "", tipo: "", tamano: "", contenedor: "", cliente_id: "", notas: "" });
  const [etapas, setEtapas] = useState(() => etapasNuevas(hoyChicago()));
  const [files, setFiles] = useState([]); // [{ file, tipo }]
  const [idExiste, setIdExiste] = useState(false);
  const [saving, setSaving] = useState(false);

  const cargarCatalogos = useCallback(async () => {
    const [cl, ub, ch] = await Promise.all([
      api.get("/clientes?solo_activos=true"),
      api.get("/ubicaciones"),
      api.get("/choferes"),
    ]);
    setClientes(cl.data);
    setUbicaciones(ub.data);
    setChoferes(ch.data);
  }, []);

  useEffect(() => { cargarCatalogos().catch((e) => toast.error(apiError(e))); }, [cargarCatalogos]);

  const cambiarDato = (campo, valor) => {
    if (campo === "tipo" && valor !== datos.tipo) setEtapas((p) => cambiarFlujo(p, valor, hoyChicago()));
    setDatos((p) => ({ ...p, [campo]: valor }));
  };

  const onFiles = (lista) => {
    const nuevos = Array.from(lista || []).map((file) => ({ file, tipo: "otro" }));
    setFiles((prev) => [...prev, ...nuevos]);
  };

  const submit = async (e) => {
    e.preventDefault();
    const problema = validarCarga(datos, etapas, { idExiste });
    if (problema) return toast.error(problema);
    setSaving(true);
    let carga;
    try {
      carga = (await api.post("/cargas", armarPayload(datos, etapas))).data;
    } catch (err) {
      toast.error(apiError(err));
      setSaving(false);
      return;
    }
    try {
      // Los documentos se suben agrupados por tipo (Delivery Order, Instrucciones u Otro).
      for (const tipo of Object.keys(TIPO_DOC_OFICINA)) {
        const grupo = files.filter((f) => f.tipo === tipo);
        if (!grupo.length) continue;
        const fd = new FormData();
        fd.append("tipo", tipo);
        grupo.forEach((f) => fd.append("files", f.file));
        await api.post(`/cargas/${carga.id}/documentos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(t("Carga creada"));
    } catch (err) {
      toast.error(t("La carga se creó, pero no se pudieron subir los documentos: {v0}", { v0: apiError(err) }));
    }
    navigate(`/panel/cargas/${carga.id}`);
  };

  return (
    <div className="p-4 lg:p-8 max-w-[860px] pb-28">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-ink-3 hover:text-ink mb-4">
        <ArrowLeft className="h-4 w-4" />{t(" Volver")}</button>
      <PageHeader titulo={t("Nueva carga")} subtitulo={t("Carga el ID, el contenedor y las etapas del recorrido. Cada etapa puede llevar un chofer distinto.")} />

      <form onSubmit={submit} className="space-y-5">
        <SectionCard titulo={t("Datos de la carga")}>
          <DatosCarga
            datos={datos}
            onChange={cambiarDato}
            clientes={clientes}
            onClienteCreado={(cli) => { setClientes((p) => [cli, ...p]); cambiarDato("cliente_id", cli.id); }}
            onIdExiste={setIdExiste}
            exportNuevo={datos.tipo === "exportacion"}
          />
        </SectionCard>

        <SectionCard titulo={t("Etapas del recorrido")}>
          <EtapasEditor etapas={etapas} onChange={setEtapas} choferes={choferes} ubicaciones={ubicaciones} abiertasInicial="todas" />
        </SectionCard>

        <SectionCard titulo={t("Documentos")} descripcion={t("Delivery Order, instrucciones u otros archivos para el chofer. Se pueden agregar después en el expediente.")}>
          <label data-testid="carga-adjuntar-btn" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }} className="flex flex-col items-center justify-center gap-1 py-6 rounded-lg border-2 border-dashed border-line-strong text-ink-3 cursor-pointer hover:border-ink hover:text-ink transition-colors">
            <Upload className="h-5 w-5" /> <span className="font-medium text-ink-2">{t("Arrastra archivos aquí o toca para adjuntar")}</span><span className="text-xs">{t("PDF, JPG o PNG")}</span>
            <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" className="hidden" onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} />
          </label>
          {files.length > 0 && (
            <div className="mt-2 space-y-1">
              {files.map((f, i) => (
                <div key={i} className="flex items-center justify-between gap-2 text-sm bg-surface-2 rounded-md px-3 py-2">
                  <span className="flex items-center gap-2 truncate text-ink-2"><FileText className="h-4 w-4 shrink-0" /> {f.file.name}</span>
                  <span className="flex items-center gap-2 shrink-0">
                    <Select value={f.tipo} onValueChange={(v) => setFiles((p) => p.map((x, j) => (j === i ? { ...x, tipo: v } : x)))}>
                      <SelectTrigger data-testid={`carga-doc-tipo-${i}`} className="h-8 w-40 bg-white text-[13px]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {Object.entries(TIPO_DOC_OFICINA).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <button type="button" aria-label={t("Quitar archivo")} onClick={() => setFiles((p) => p.filter((_, j) => j !== i))} className="text-ink-3 hover:text-danger"><X className="h-4 w-4" /></button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <div className="fixed bottom-0 inset-x-0 lg:left-[236px] z-20 bg-white border-t border-line px-4 lg:px-8 py-3 flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>{t("Cancelar")}</Button>
          <Button type="submit" data-testid="carga-guardar" className="px-8" disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Guardar carga")}
          </Button>
        </div>
      </form>
    </div>
  );
}
